import { ModalCustom } from "components/ModalCustom";
import { PrimaryButton } from "components/Button/style";
import { FaCopy, FaDownload, FaEye } from "react-icons/fa";
import { toastErro, toastSucesso } from "components/Toast";
import { formatarDinheiro } from "_utils/Money";
import { formatarCpfCnpj } from "_utils/CpfCnpj";
import { OptionsBancos } from "features/core/pages/Financeiro/Constants/OptionsBancos";
import { IDadosPagamento } from "../../../../../interface/IDadosPagamento";
import {
  BaixarTodos,
  Bloco,
  BoletoAcaoBotao,
  BoletoAcoes,
  BoletoBloco,
  BoletoCabecalho,
  BoletoItem,
  BoletoLista,
  BoletoNome,
  Campo,
  Label,
  Secao,
  SecaoTitulo,
  Valor,
} from "./style";

interface IProps {
  show: boolean;
  dados: IDadosPagamento | null;
  handleClose: () => void;
  onVisualizarBoleto: (idLote: number, idDocumento: number) => void;
  onBaixarBoleto: (idLote: number, idDocumento: number, nome: string) => void;
  onBaixarTodosBoletos: (idLote: number) => void;
}

function copiarTexto(valor: string) {
  if (window.isSecureContext && navigator.clipboard) {
    navigator.clipboard
      .writeText(valor)
      .then(() => toastSucesso("Dados copiados"))
      .catch(() =>
        toastErro(
          "Não foi possível copiar automaticamente. Selecione o texto e copie com o teclado (Ctrl+C).",
        ),
      );
    return;
  }
  toastErro(
    "Não foi possível copiar automaticamente. Por favor, selecione o texto e copie com o teclado (Ctrl+C).",
  );
}

function CampoSimples({
  label,
  valor,
}: {
  label: string;
  valor: string | null;
}) {
  return (
    <Campo>
      <Label>{label}</Label>
      <Valor>{valor ?? "-"}</Valor>
    </Campo>
  );
}

function CampoCopiavel({
  label,
  valor,
}: {
  label: string;
  valor: string | null;
}) {
  return (
    <Campo>
      <Label>{label}</Label>
      <Valor>
        {valor ?? "-"}
        {valor && <FaCopy onClick={() => copiarTexto(valor)} title="Copiar" />}
      </Valor>
    </Campo>
  );
}

function formatarBanco(banco: string | null): string {
  if (!banco) return "-";
  const opcao = OptionsBancos.find((o) => o.value === String(banco));
  return opcao ? opcao.label : banco;
}

export function ModalDadosPagamento({
  show,
  dados,
  handleClose,
  onVisualizarBoleto,
  onBaixarBoleto,
  onBaixarTodosBoletos,
}: IProps) {
  return (
    <ModalCustom
      show={show}
      handleClose={handleClose}
      centered
      size="lg"
      title="Dados para pagamento fornecedor"
    >
      {dados && (
        <>
          <Secao>
            <SecaoTitulo>Dados do pagamento</SecaoTitulo>
            <Bloco>
              <Campo>
                <Label>NF/Recibo</Label>
                <Valor>{dados.numeroNfRecibo ?? "-"}</Valor>
              </Campo>
              <Campo>
                <Label>Total a pagar</Label>
                <Valor>
                  {dados.valorTotalNfRecibo != null
                    ? formatarDinheiro(Number(dados.valorTotalNfRecibo))
                    : "-"}
                </Valor>
              </Campo>
              <Campo>
                <Label>Fornecedor</Label>
                <Valor>{dados.nomeFornecedor ?? "-"}</Valor>
              </Campo>
              <Campo>
                <Label>CNPJ</Label>
                <Valor>
                  {dados.cpfCnpj ? formatarCpfCnpj(dados.cpfCnpj) : "-"}
                </Valor>
              </Campo>
            </Bloco>
          </Secao>

          {(dados.banco ||
            dados.agencia ||
            dados.conta ||
            dados.titularContaTransferencia ||
            dados.cpfCnpjTitularContaTransferencia) && (
            <Secao>
              <SecaoTitulo>Transferência bancária</SecaoTitulo>
              <Bloco>
                <CampoSimples label="Banco" valor={formatarBanco(dados.banco)} />
                <CampoSimples label="Agência" valor={dados.agencia} />
                <CampoCopiavel
                  label="CNPJ"
                  valor={
                    dados.cpfCnpjTitularContaTransferencia
                      ? formatarCpfCnpj(dados.cpfCnpjTitularContaTransferencia)
                      : null
                  }
                />
                <CampoCopiavel label="Conta" valor={dados.conta} />
                <CampoCopiavel
                  label="Nome"
                  valor={dados.titularContaTransferencia}
                />
              </Bloco>
            </Secao>
          )}

          {(dados.chavePix || dados.titularContaPix) && (
            <Secao>
              <SecaoTitulo>PIX</SecaoTitulo>
              <Bloco>
                <CampoCopiavel label="Chave PIX" valor={dados.chavePix} />
                <CampoCopiavel
                  label="Nome"
                  valor={dados.titularContaPix}
                />
              </Bloco>
            </Secao>
          )}

          {dados.boletos.length > 0 && (
            <Secao>
              <SecaoTitulo>Boletos</SecaoTitulo>
              <BoletoBloco>
                <BaixarTodos
                  type="button"
                  onClick={() => onBaixarTodosBoletos(dados.idLote)}
                >
                  <FaDownload /> Baixar todos os boletos
                </BaixarTodos>
                <BoletoCabecalho>
                  <span>Descrição</span>
                  <span>Ações</span>
                </BoletoCabecalho>
                <BoletoLista>
                  {dados.boletos.map((boleto) => (
                    <BoletoItem key={boleto.id}>
                      <BoletoNome>{boleto.nome}</BoletoNome>
                      <BoletoAcoes>
                        <BoletoAcaoBotao
                          type="button"
                          title="Visualizar"
                          onClick={() => onVisualizarBoleto(dados.idLote, boleto.id)}
                        >
                          <FaEye />
                        </BoletoAcaoBotao>
                        <BoletoAcaoBotao
                          type="button"
                          title="Baixar"
                          onClick={() => onBaixarBoleto(dados.idLote, boleto.id, boleto.nome)}
                        >
                          <FaDownload />
                        </BoletoAcaoBotao>
                      </BoletoAcoes>
                    </BoletoItem>
                  ))}
                </BoletoLista>
              </BoletoBloco>
            </Secao>
          )}
        </>
      )}
    </ModalCustom>
  );
}
